import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Spinner } from "react-bootstrap";
import { useNavigate, Link, useParams } from "react-router-dom";
import { emailregex, mobileregex } from "../../utils/Regex";
import { toast } from "react-toastify";
import {
  getOrganization,
  updateOrganization,
} from "../../store/slices/organization";
import { useDispatch } from "react-redux";
import { uploadFile } from "../../utils/helpers/fileupload";
import { FaImage } from "react-icons/fa";
import { IoMdRemoveCircle } from "react-icons/io";

export default function EditOrganization() {
  const user = JSON.parse(localStorage.getItem("user"));
  const token = user?.data?.accessToken;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    email: "",
    mobile: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const fetchOrganization = async () => {
    try {
      const response = await dispatch(getOrganization({ id })).unwrap();

      const orgData = response?.data?.[0];

      setFormData({
        name: orgData?.name || "",
        type: orgData?.type || "",
        email: orgData?.email || "",
        mobile: orgData?.mobileNumber || "",
        logo: orgData?.logo || "",
      });

      // 🔹 Set preview if logo exists
      if (orgData?.logo) {
        setPreview(orgData.logo);
      }
    } catch (error) {
      toast.error(error?.message || "Failed to load organization");
    }
  };
  useEffect(() => {
    if (id) {
      fetchOrganization();
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validate = () => {
    let newErrors = {};

    if (!formData.name.trim())
      newErrors.name = "Please enter organization name";

    if (!formData.type.trim())
      newErrors.type = "Please select organization type";

    if (!formData.email.trim()) newErrors.email = "Please enter email";
    else if (!emailregex.test(formData.email))
      newErrors.email = "Please enter valid email";

    if (!formData.mobile.trim())
      newErrors.mobile = "Please enter mobile number";
    else if (!mobileregex.test(formData.mobile))
      newErrors.mobile = "Please enter valid mobile number";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024;

    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        image: "Image size must be less than 5MB",
      }));

      return;
    }

    setImage(file);
    setPreview(URL.createObjectURL(file));

    setErrors((prev) => {
      const copy = { ...prev };
      delete copy.image;
      return copy;
    });
  };

  const removeImage = () => {
    setImage(null);
    setPreview(null);
  };

  const submitOrganization = async () => {
    try {
      setLoading(true);
      let fileUrl = "";

      if (image) {
        fileUrl = await uploadFile(image, token);
      }

      const payload = {
        id,
        name: formData.name,
        type: formData.type,
        email: formData.email,
        mobileNumber: formData.mobile,
        logo: fileUrl,
      };

      await dispatch(updateOrganization(payload)).unwrap();

      toast.success("Organization updated successfully");

      navigate("/superadmin/organization");
    } catch (error) {
      toast.error(error?.message || "Failed to update organization");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    submitOrganization();
  };

  return (
    <>
      <div className="breadcrumb-bar mb-2">
        <div className="breadcrumb-left">
          <Link to="/superadmin/home" className="breadcrumb-link active">
            Home
          </Link>
          <span className="breadcrumb-separator">|</span>
          <Link
            className="breadcrumb-link active"
            to="/superadmin/organization"
          >
            Organization
          </Link>
          <span className="breadcrumb-separator">|</span>
          <span className="breadcrumb-link inactive">Edit Organization</span>
        </div>
      </div>

      <Container fluid className="dashboard-content">
        <Row>
          <Col xs={12}>
            <Card
              className="dashboard-card shadow-sm"
              style={{ border: "none" }}
            >
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col lg={4} md={6} sm={12} className="mb-3">
                      <Form.Group>
                        <Form.Label>Organization Name</Form.Label>
                        <div className="position-relative">
                          <Form.Control
                            type="text"
                            name="name"
                            placeholder="Enter organization name"
                            value={formData.name}
                            onChange={handleChange}
                          />
                        </div>

                        {errors.name && (
                          <small className="text-danger">{errors.name}</small>
                        )}
                      </Form.Group>
                    </Col>

                    <Col lg={4} md={6} sm={12} className="mb-3">
                      <Form.Group>
                        <Form.Label>Organization Type</Form.Label>
                        <div className="position-relative">
                          <Form.Select
                            name="type"
                            value={formData.type}
                            onChange={handleChange}
                          >
                            <option value="">Select organization type</option>
                            <option value="TRAVEL_AGENCY">Travel Agency</option>
                            <option value="HOTEL">Hotel</option>
                            <option value="CORPORATE">Corporate</option>
                            <option value="VENDOR">Vendor</option>
                          </Form.Select>
                        </div>

                        {errors.type && (
                          <small className="text-danger">{errors.type}</small>
                        )}
                      </Form.Group>
                    </Col>

                    <Col lg={4} md={6} sm={12} className="mb-3">
                      <Form.Group>
                        <Form.Label>Email</Form.Label>
                        <div className="position-relative">
                          <Form.Control
                            type="text"
                            name="email"
                            placeholder="Enter email"
                            value={formData.email}
                            onChange={handleChange}
                          />
                        </div>

                        {errors.email && (
                          <small className="text-danger">{errors.email}</small>
                        )}
                      </Form.Group>
                    </Col>

                    <Col lg={4} md={6} sm={12} className="mb-3">
                      <Form.Group>
                        <Form.Label>Mobile Number</Form.Label>
                        <div className="position-relative">
                          <Form.Control
                            type="text"
                            name="mobile"
                            placeholder="Enter mobile number"
                            value={formData.mobile}
                            onChange={handleChange}
                          />
                        </div>

                        {errors.mobile && (
                          <small className="text-danger">{errors.mobile}</small>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Image</Form.Label>

                        <div
                          className="custom-file-uploader"
                          onClick={() =>
                            document.getElementById("imageUpload").click()
                          }
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            const file = e.dataTransfer.files[0];
                            handleImageChange({ target: { files: [file] } });
                          }}
                        >
                          {!preview ? (
                            <div className="upload-placeholder">
                              <div className="upload-icon-box">
                                <FaImage className="upload-icon-white" />
                              </div>
                              <p className="mb-1">Click or Drag Image Here</p>
                            </div>
                          ) : (
                            <div className="image-preview-wrapper">
                              <img
                                src={
                                  preview.startsWith("http")
                                    ? preview
                                    : `${preview}`
                                }
                                alt="preview"
                                className="preview-img"
                              />
                              <IoMdRemoveCircle
                                size={24}
                                className="remove-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeImage();
                                }}
                              />
                            </div>
                          )}

                          <Form.Control
                            id="imageUpload"
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            hidden
                          />
                        </div>

                        {errors.image && (
                          <small className="text-danger d-block mt-2">
                            {errors.image}
                          </small>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="d-flex  mb-5 justify-content-center gap-3 mt-5">
                    <button
                      type="button"
                      className="cancel-btn"
                      onClick={() => navigate(-1)}
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      className="submit-btn"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            variant="light"
                            className="me-2"
                          />
                          Submitting...
                        </>
                      ) : (
                        "Submit"
                      )}
                    </button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
